<template>
  <div class="tableRoot">
    <div class="ui grid">
      <div class="left floated four wide column">
        <div class="ui tiny left icon input" :class="{ 'right labeled': searchTerm }">
          <i class="search icon"></i>
          <input
            id="searchInput"
            type="text"
            :placeholder="$t('SEARCH')"
            v-model="searchTerm"
            autocomplete="off"
          />
          <div class="ui label" v-if="searchTerm">
            {{ $tc('ROW', sortedFilteredRows.length) }}
          </div>
        </div>
      </div>

      <!-- Manipulate rows and columns AND paging -->
      <div class="right floated right aligned twelve wide column">
        <!-- Add/remove rows -->
        <div class="ui tiny icon buttons">
          <a
            class="ui green icon button"
            :class="{ disabled: isOutdated || isDiff }"
            @click="insertRow()"
            :title="$t('ADD', { thing: $tc('ROW', 1) })"
          >
            <i class="icon plus"></i>
          </a>
          <a class="ui disabled button">{{ $tc('ROW', rows.length) }}</a>
          <a
            class="ui red icon button"
            :class="{
              disabled: selectedRows.length === 0 || isOutdated || isDiff
            }"
            @click="deleteSelectedRows()"
            :disabled="isOutdated || isDiff"
            :title="$t('DELETE', { thing: $tc('ROW', 1) })"
          >
            <i class="minus icon"></i>
          </a>
        </div>

        <!-- Add/remove columns -->
        <div class="ui tiny icon buttons">
          <a
            class="ui green icon button"
            :class="{ disabled: focusedCol == null || isOutdated || isDiff }"
            @click="insertColumn()"
            :title="$t('ADD', { thing: $tc('COLUMN', 1) })"
          >
            <i class="icon plus"></i>
          </a>
          <a class="ui disabled button">{{ $tc('COLUMN', header.length) }}</a>
          <a
            class="ui red icon button"
            :class="{
              disabled:
                focusedCol == null ||
                header.length <= 1 ||
                isOutdated ||
                isDiff ||
                (header[focusedCol] && header[focusedCol].fixed)
            }"
            @click="deleteColumn()"
            :disabled="isOutdated || isDiff"
            :title="$t('DELETE', { thing: $tc('COLUMN', 1) })"
          >
            <i class="minus icon"></i>
          </a>
        </div>

        <!-- Reorganize rows and columns -->
        <div class="ui tiny icon buttons">
          <a
            class="ui icon button"
            :class="{
              disabled:
                focusedCol == null ||
                focusedCol === 0 ||
                isOutdated ||
                isDiff ||
                (header[focusedCol] && header[focusedCol].fixed)
            }"
            @click="shiftColumnLeft(focusedCol)"
            :title="$t('MOVE', { thing: $tc('COLUMN', 1), direction: $t('DIRECTION_LEFT') })"
          >
            <i class="icon left arrow"></i>
          </a>
          <a
            class="ui icon button"
            :class="{
              disabled:
                focusedCol == null ||
                focusedCol === header.length - 1 ||
                isOutdated ||
                isDiff ||
                (header[focusedCol] && header[focusedCol].fixed)
            }"
            @click="shiftColumnRight(focusedCol)"
            :title="$t('MOVE', { thing: $tc('COLUMN', 1), direction: $t('DIRECTION_RIGHT') })"
          >
            <i class="icon right arrow"></i>
          </a>
          <a
            class="ui icon button"
            :class="{
              disabled:
                focusedRow == null ||
                focusedRow === 0 ||
                focusedRow === Number.MIN_VALUE ||
                isOutdated ||
                isDiff
            }"
            @click="shiftRowUp(focusedRow)"
            :title="$t('MOVE', { thing: $tc('ROW', 1), direction: $t('DIRECTION_UP') })"
          >
            <i class="icon up arrow"></i>
          </a>
          <a
            class="ui icon button"
            :class="{
              disabled:
                focusedRow == null ||
                focusedRow === this.rows.length - 1 ||
                focusedRow === Number.MIN_VALUE ||
                isOutdated ||
                isDiff
            }"
            @click="shiftRowDown(focusedRow)"
            :title="$t('MOVE', { thing: $tc('ROW', 1), direction: $t('DIRECTION_DOWN') })"
          >
            <i class="icon down arrow"></i>
          </a>
        </div>

        <!-- Paging -->
        <div class="ui tiny buttons right">
          <a
            class="ui icon button"
            @click="page = 0"
            :class="{ disabled: page === 0 || isOutdated || isDiff }"
            :disabled="page === 0 || isOutdated || isDiff"
            :title="$t('FIRST_PAGE')"
          >
            <i class="left double angle icon"></i>
          </a>
          <a
            class="ui icon button"
            @click="prevPage()"
            :class="{ disabled: page === 0 || isOutdated || isDiff }"
            :disabled="page === 0 || isOutdated || isDiff"
            :title="$t('PREV_PAGE')"
          >
            <i class="left angle icon"></i>
          </a>
          <div class="ui disabled button">{{ page + 1 }} / {{ totalPages + 1 }}</div>
          <a
            class="ui icon button"
            @click="nextPage()"
            :class="{ disabled: page === totalPages || isOutdated || isDiff }"
            :disabled="page === totalPages || isOutdated || isDiff"
            :title="$t('NEXT_PAGE')"
          >
            <i class="right angle icon"></i>
          </a>
          <a
            class="ui icon button"
            @click="page = totalPages"
            :class="{ disabled: page === totalPages || isOutdated || isDiff }"
            :disabled="page === totalPages || isOutdated || isDiff"
            :title="$t('LAST_PAGE')"
          >
            <i class="right double angle icon"></i>
          </a>
        </div>
        <div class="ui tiny input">
          <input
            type="number"
            min="1"
            v-model="pageSize"
            style="width: 70px;"
            :disabled="isOutdated || isDiff"
          />
        </div>
      </div>
    </div>
    <div id="rowArea">
      <Spinny :promise="loading" />
      <div
        id="noRows"
        class="ui two column centered grid"
        v-if="!loading && ((rows.length === 0 && searchTerm) || header.length === 0)"
      >
        <div class="center aligned column" style="margin: auto;">
          {{ $t('NO_ROWS') }}
        </div>
      </div>
      <div
        id="noRowsMatch"
        class="ui two column centered grid"
        v-if="rows.length !== 0 && rowsOnPage.length === 0"
      >
        <div class="center aligned column" style="margin: auto;">
          {{ $t('NO_MATCH', { term: searchTerm }) }}
        </div>
      </div>
      <table class="ui compact celled sortable definition table" v-if="shouldTableBeShown">
        <thead>
          <tr>
            <th id="selectAllRows" class="collapsing center aligned" @click="selectAllRows()">
              <i
                class="square outline icon"
                :class="{ check: rows.length !== 0 && selectedRows.length === rows.length }"
              />
            </th>
            <th v-for="(h, colIdx) in headerToDisplay" :key="colIdx" :style="styleCell(h)">
              <div class="ui transparent input" style="width: 100%;">
                <input
                  style="margin-right: -5px;"
                  v-model="h.v"
                  @keyup.ctrl.shift.a="addHeader(colIdx)"
                  @keyup.ctrl.shift.delete="deleteHeader(colIdx)"
                  @focus="onFocus(Number.MIN_VALUE, colIdx)"
                  @blur="onBlur(Number.MIN_VALUE, colIdx)"
                  :disabled="isOutdated || isDiff || h.disabled"
                />
                <i
                  class="sort icon"
                  :class="{
                    ascending: sorting.column === colIdx && sorting.asc,
                    descending: sorting.column === colIdx && !sorting.asc,
                    grey: sorting.column !== colIdx,
                    blue: sorting.column === colIdx
                  }"
                  style="float: right !important;"
                  @click="sortBy(colIdx)"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(r, rowIdx) in rowsOnPage"
            :key="rowIdx"
            :class="{ active: selectedRows.indexOf(rowIdx + page * pageSize) >= 0 }"
          >
            <td class="collapsing center aligned" @click="selectRow(rowIdx + page * pageSize)">
              <i
                class="square outline icon"
                :class="{ check: selectedRows.indexOf(rowIdx + page * pageSize) >= 0 }"
              />
            </td>
            <td v-for="(c, colIdx) in r" :key="colIdx" :style="styleCell(c)">
              <div class="ui transparent input" style="width: 100%;">
                <input
                  class="monospaced"
                  v-model="c.v"
                  :ref="rowIdx + ',' + colIdx"
                  @keyup.alt.down="changeFocus(rowIdx + 1, colIdx)"
                  @keyup.alt.up="changeFocus(rowIdx - 1, colIdx)"
                  @keyup.alt.right="changeFocus(rowIdx, colIdx + 1)"
                  @keyup.alt.left="changeFocus(rowIdx, colIdx - 1)"
                  @blur="onBlur(rowIdx, colIdx)"
                  @focus="onFocus(rowIdx, colIdx)"
                  @keyup.ctrl.shift.a="insertRow()"
                  @keyup.ctrl.shift.delete="deleteRow(rowIdx)"
                  @keyup.ctrl.x.exact="selectRow(rowIdx)"
                  @keyup.esc.exact="cancelEdit()"
                  :disabled="isOutdated || isDiff || c.disabled"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

.tableRoot {
  display: flex;
  flex-direction: column;

  #rowArea {
    flex: 1 0 100px;
    overflow-y: scroll;
    margin: 10px 0 0 0;
    padding-bottom: 1px;
  }
}

#noRows,
#noRowsMatch {
  width: 100%;
  height: 100%;
}

th#selectAllRows {
  pointer-events: auto;
}

td:focus-within {
  box-shadow: inset 0 0 0 1px rgba(34, 132, 209, 0.5);
  background-color: rgba(34, 132, 209, 0.2);
}

td,
th > div.transparent.input > input {
  padding: 0px !important;
}
</style>

<script>
// @flow
import _ from 'lodash'
import Vue from 'vue'
import { Spinny } from 'ui'

export default {
  name: 'data-table',
  components: {},
  props: {
    initialRows: {
      type: Array,
      default: () => []
    },
    initialHeader: {
      type: Array,
      default: () => []
    },
    maxHeight: {
      type: Number,
      default: () => window.innerHeight - 65
    },
    tableUpdatedHandler: {
      type: Function,
      default: () => () => {}
    },
    isOutdated: {
      type: Boolean,
      default: () => false
    },
    isDiff: {
      type: Boolean,
      default: () => false
    },
    diff: {
      type: Object,
      default: () => {}
    },
    styler: {
      type: Function,
      default: () => () => {}
    },
    loading: {
      type: Promise,
      default: () => null
    }
  },

  components: { Spinny },

  data() {
    return {
      rows: this.initialRows,
      header: this.initialHeader,
      rowsDiff: [],
      headerDiff: [],
      editing: false,
      sorted: [],
      searchTerm: '',
      isInSearchMode: false,
      page: 0,
      pageBeforeSearch: 0,
      actualPageSize: 25,
      sorting: { column: -1, asc: true },
      selectedRows: [],
      focusedRow: null,
      focusedCol: null
    }
  },

  computed: {
    pageSize: {
      get: function() {
        return this.actualPageSize
      },
      set: function(newSize) {
        if (newSize > 0) {
          this.actualPageSize = newSize
        }
      }
    },
    shouldTableBeShown() {
      return (
        this.header.length !== 0 &&
        ((this.rows.length !== 0 && !this.searchTerm) ||
          this.rowsOnPage.length > 0 ||
          (this.rows.length === 0 && !this.searchTerm))
      )
    },
    filteredRows() {
      if (this.searchTerm && this.searchTerm !== '') {
        if (!this.isInSearchMode) {
          this.isInSearchMode = true
          this.pageBeforeSearch = this.page
          this.page = 0
        }
        return _.filter(this.rowsToDisplay, row => {
          return _.some(row, cell => cell.v && cell.v.indexOf(this.searchTerm) >= 0)
        })
      } else {
        if (this.isInSearchMode) {
          this.isInSearchMode = false
          this.page = this.pageBeforeSearch
          this.pageBeforeSearch = 0
        }
        return this.rowsToDisplay
      }
    },

    sortedFilteredRows() {
      if (
        !this.editing &&
        this.sorting.column >= 0 &&
        this.sorting.column < this.headerToDisplay.length
      ) {
        let sorted = _.sortBy(
          this.filteredRows,
          r =>
            r[this.sorting.column] &&
            (parseFloat(r[this.sorting.column].v) || r[this.sorting.column].v)
        )
        if (this.sorting.asc) {
          this.sorted = sorted
        } else {
          this.sorted = _.reverse(sorted)
        }
      } else if (!this.editing) {
        this.sorted = this.filteredRows
      }
      return this.sorted
    },

    rowsOnPage() {
      let start = this.page * this.pageSize
      return _.slice(
        this.sortedFilteredRows,
        start,
        start + Math.min(this.pageSize, this.sortedFilteredRows.length)
      )
    },

    headerToDisplay() {
      return this.isDiff ? this.headerDiff : this.header
    },

    rowsToDisplay() {
      return this.isDiff ? this.rowsDiff : this.rows
    },

    totalPages() {
      return Math.max(0, Math.floor(this.sortedFilteredRows.length / this.pageSize))
    },
    firstRowOnPageIndex() {
      return this.page * this.pageSize
    }
  },

  methods: {
    selectRow(row) {
      const idx = this.selectedRows.indexOf(row)
      if (idx < 0) {
        this.selectedRows.push(row)
      } else {
        this.selectedRows.splice(idx, 1)
      }
    },
    selectAllRows() {
      if (this.selectedRows.length === this.rows.length) {
        this.selectedRows = []
      } else {
        this.selectedRows = _.map(this.rows, (v, idx) => idx)
      }
    },

    changeFocus(row, col) {
      let newRow
      if (row >= 0) {
        newRow = (((row + this.rows.length) % this.rows.length) + this.pageSize) % this.pageSize
        if (row >= this.pageSize) this.nextPage()
      } else {
        newRow = this.pageSize - 1
        this.prevPage()
      }
      let newCol = (col + this.rows[newRow].length) % this.rows[newRow].length
      let focusedCell = this.$refs[`${newRow},${newCol}`]
        ? this.$refs[`${newRow},${newCol}`][0]
        : null
      if (!focusedCell) {
        this.tryChangeFocusAfterDelay(newRow, newCol, 1)
      } else {
        this.focusOnNewCell(focusedCell, newRow, newCol)
      }
    },
    focusOnNewCell(cell, newRow, newCol) {
      cell.focus()
      cell.select()
      this.focusedRow = newRow + this.page * this.pageSize
      this.focusedCol = newCol
    },
    tryChangeFocusAfterDelay(newRow, newCol, infiniteLoopSafetyCounter) {
      if (infiniteLoopSafetyCounter < 100) {
        _.delay(() => {
          let focusedCell = this.$refs[`${newRow},${newCol}`][0]
          if (focusedCell) {
            this.focusOnNewCell(focusedCell, newRow, newCol)
          } else {
            this.tryChangeFocusAfterDelay(newRow, newCol, infiniteLoopSafetyCounter++)
          }
        }, 1)
      } else {
        console.error('Focused cell not found after 100 loops', newRow, newCol)
      }
    },

    sortBy(colIdx) {
      this.selectedRows = []
      this.sorting = { column: colIdx, asc: !this.sorting.asc }
    },

    onFocus(rowIdx, colIdx) {
      this.focusedRow = rowIdx + this.page * this.pageSize
      this.focusedCol = colIdx
      this.editing = true
    },
    onBlur(rowIdx, colIdx) {
      this.delayClearingFocusedRowAndColForButtonClicked(rowIdx, colIdx)
      this.editing = false
    },
    delayClearingFocusedRowAndColForButtonClicked(rowIdx, colIdx) {
      _.delay(() => {
        if (this.focusedRow - this.page * this.pageSize === rowIdx && this.focusedCol === colIdx) {
          this.clearFocusedRowAndCol()
        }
      }, 250)
    },
    cancelEdit() {
      this.editing = false
    },
    addHeader(colIdx) {
      const newColIdx = colIdx + 1
      this.header.splice(newColIdx, 0, { v: '' })
      this.rows.forEach(r => r.splice(newColIdx, 0, { v: '' }))
      this.tableUpdatedHandler(this.header, this.rows)
    },
    deleteHeader(colIdx) {
      if (this.header.length > 1) {
        this.header.splice(colIdx, 1)
        this.rows.forEach(r => r.splice(colIdx, 1))
        this.tableUpdatedHandler(this.header, this.rows)
      }
    },
    deleteRow(rowIdx) {
      this.rows.splice(rowIdx, 1)
      this.tableUpdatedHandler(this.header, this.rows)
    },
    deleteSelectedRows() {
      this.selectedRows.forEach(rowIndex => {
        this.sortedFilteredRows[rowIndex].deleted = true
      })
      this.rows = _.reject(this.rows, { deleted: true })
      this.selectedRows = []

      this.tableUpdatedHandler(this.header, this.rows)
    },
    insertRow() {
      if (this.header.length === 0) this.header.push([{ v: '' }])
      const insertRowPosition =
        this.focusedRow !== null ? this.focusedRow + 1 : this.firstRowOnPageIndex

      this.rows.splice(
        insertRowPosition,
        0,
        _.map(this.header, h => {
          return { v: '' }
        })
      )
      if (this.focusedRow && insertRowPosition % this.pageSize === 0) {
        this.nextPage()
      }

      this.tableUpdatedHandler(this.header, this.rows)
      this.changeFocus(insertRowPosition % this.pageSize, this.focusedCol)
    },
    shiftRowUp(rowIdx) {
      this.shiftRow(rowIdx, -1)
    },
    shiftRowDown(rowIdx) {
      this.shiftRow(rowIdx, 1)
    },
    shiftRow(rowIdx, direction) {
      if (rowIdx + direction >= 0 && rowIdx + direction < this.rows.length) {
        const focusedCol = this.focusedCol
        const a = this.rows[rowIdx]
        Vue.set(this.rows, rowIdx, this.rows[rowIdx + direction])
        Vue.set(this.rows, rowIdx + direction, a)
        this.changeFocus(rowIdx + direction, focusedCol)
        this.tableUpdatedHandler(this.header, this.rows)
      }
    },
    insertColumn() {
      this.addHeader(this.focusedCol)
      this.changeFocus(this.focusedRow, this.focusedCol + 1)
    },
    deleteColumn() {
      this.deleteHeader(this.focusedCol)
      this.changeFocus(this.focusedRow, this.focusedCol)
    },
    shiftColumnLeft(colIdx) {
      this.shiftColumn(colIdx, -1)
    },
    shiftColumnRight(colIdx) {
      this.shiftColumn(colIdx, 1)
    },
    shiftColumn(colIdx, direction) {
      if (this.header[colIdx].fixed) return
      if (colIdx + direction >= 0 && colIdx + direction < this.header.length) {
        const focusedRow = this.focusedRow
        _.each(_.concat([this.header], this.rows), row => {
          const a = row[colIdx]
          Vue.set(row, colIdx, row[colIdx + direction])
          Vue.set(row, colIdx + direction, a)
        })
        this.changeFocus(focusedRow, colIdx + direction)
        this.tableUpdatedHandler(this.header, this.rows)
      }
    },
    prevPage() {
      this.page = Math.max(0, this.page - 1)
      this.clearFocusedRowAndCol()
    },
    nextPage() {
      this.page = Math.min(this.totalPages, this.page + 1)
      this.clearFocusedRowAndCol()
    },
    clearFocusedRowAndCol() {
      this.focusedRow = null
      this.focusedCol = null
    },
    styleCell(cell) {
      if (cell.action === 'changed') {
        return {
          'background-color': '#f9df59 !important'
        }
      } else if (cell.action === 'added') {
        return {
          'background-color': '#9BE0AB !important'
        }
      } else if (cell.action === 'removed') {
        return {
          'background-color': '#EF9E9E !important'
        }
      } else {
        return (cell.style && cell.style()) || this.styler(cell)
      }
    }
  },
  watch: {
    initialRows: function(rows) {
      if (rows !== this.rows) {
        this.page = 0
        this.rows = rows
        this.selectedRows = []
        this.sorting = { column: -1, asc: true }
        this.searchTerm = ''
        this.clearFocusedRowAndCol()
      }
    },
    initialHeader: function(header) {
      this.header = header
    },
    diff: function(diff) {
      this.rowsDiff = diff ? diff.rowsDiff : []
      this.headerDiff = diff ? diff.headerDiff : []
    }
  }
}
</script>
